import AppContext from "PFApp/app_context";
import { getAPIFilters } from "PFApp/use_filtered_collection/format_filters";
import { useMarketplaceInterestedActivities } from "PFCore/hooks/queries/activities/marketplace/use_marketplace_interested_activities";
import { useMarketplaceSavedActivities } from "PFCore/hooks/queries/activities/marketplace/use_marketplace_saved_activities";
import { useMarketplaceSharedActivities } from "PFCore/hooks/queries/activities/marketplace/use_marketplace_shared_activities";
import { useMatchingActivities } from "PFCore/hooks/queries/activities/matching_activities/use_matching_activities";
import { MarketplaceActivitiesParams } from "PFCore/services/marketplace/marketplace.types";
import { useContext } from "react";

import { MatchingRelationType } from "../../constants/matching_relation_type";
import { MarketplacePage, WorkOpportunitiesTab } from "../constants";
import { useMarketplaceContext } from "../contexts/marketplace_context";
import { getTabFilters } from "../helpers/get_tab_filters";
import { useMatchingActivitiesFilters } from "./use_matching_activities_filters";
import { useUnreadActivitiesFilters } from "./use_unread_activities_filters";

type UseMarketplaceCountersParams = {
  observerId?: MarketplacePage;
};

export const useMarketplaceCounters = ({ observerId }: UseMarketplaceCountersParams = {}) => {
  const {
    store: { currentProfile }
  } = useContext(AppContext);
  const { relationType, filtersData } = useMarketplaceContext();
  const matchingActivitiesFilters = useMatchingActivitiesFilters();
  const unreadActivitiesFilters = useUnreadActivitiesFilters();

  const profileId = currentProfile?.id;

  const commonParams: MarketplaceActivitiesParams = {
    page: 1,
    perPage: 0
  };

  const { data: matchedActivitiesData } = useMatchingActivities(
    {
      ...commonParams,
      profileId,
      filters: getAPIFilters(matchingActivitiesFilters),
      relationType
    },
    { keepPreviousData: true }
  );

  const { data: interestedActivitiesData } = useMarketplaceInterestedActivities(
    {
      ...commonParams,
      filters: getAPIFilters(getTabFilters(filtersData, WorkOpportunitiesTab.Interested))
    },
    { keepPreviousData: true }
  );

  const { data: savedActivitiesData } = useMarketplaceSavedActivities(
    {
      ...commonParams,
      filters: getAPIFilters(getTabFilters(filtersData, WorkOpportunitiesTab.SavedRoles))
    },
    { keepPreviousData: true }
  );

  const { data: sharedActivitiesData } = useMarketplaceSharedActivities(
    {
      ...commonParams,
      filters: getAPIFilters(getTabFilters(filtersData, WorkOpportunitiesTab.SharedWithMe))
    },
    { keepPreviousData: true }
  );

  const { data: unreadActivitiesData } = useMatchingActivities(
    {
      ...commonParams,
      profileId,
      filters: getAPIFilters(unreadActivitiesFilters),
      relationType:
        observerId === MarketplacePage.Dashboard ? MatchingRelationType.PrefilteredAndAvailable : relationType
    },
    { keepPreviousData: true }
  );

  return {
    matchingActivitiesCount: matchedActivitiesData?.meta.total,
    interestedActivitiesCount: interestedActivitiesData?.meta.total,
    savedActivitiesCount: savedActivitiesData?.meta.total,
    sharedActivitiesCount: sharedActivitiesData?.meta.total,
    unreadActivitiesCount: unreadActivitiesData?.meta.total
  };
};
